import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, Typography, Box, Button, IconButton, Grid } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { useDispatch } from 'react-redux';
import { updateCompany } from '../../../redux/company/actions';
import { Plus, Image as ImageIcon, X } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

interface ICompanyGalleryEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const CompanyGalleryEdit: React.FC<ICompanyGalleryEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState({
        gallery: company?.gallery || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
    const [galleryImages, setGalleryImages] = useState<string[]>([]);

    useEffect(() => {
        if (company) {
            const newData = {
                gallery: company.gallery || [],
            };
            setFormData(newData);
            setInitialData(newData);
            setGalleryImages(company.gallery || []);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleSave = () => {
        if (company?._id) {
            const payload: any = {
                id: company._id,
                body: {
                    gallery: galleryFiles.length > 0 ? galleryFiles : formData.gallery,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    // Register the save function with the parent container
    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id, galleryFiles]);

    // Compression function from PhotoUploadModal
    const compressImage = (base64: string, quality = 0.7): Promise<string> => {
        return new Promise(resolve => {
            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0);
                canvas.toBlob(
                    blob => {
                        if (blob) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                resolve(reader.result as string);
                            };
                            reader.readAsDataURL(blob);
                        } else {
                            resolve(base64);
                        }
                    },
                    'image/jpeg',
                    quality,
                );
            };
            img.src = base64;
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const newFiles: File[] = [];
            const newImages: string[] = [];

            for (const file of Array.from(files)) {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    const result = await new Promise<string>(resolve => {
                        reader.onload = async e => {
                            const base64 = e.target?.result as string;
                            if (base64) {
                                // Compress the image
                                const compressedBase64 = await compressImage(base64, 0.7);
                                resolve(compressedBase64);
                            }
                        };
                        reader.readAsDataURL(file);
                    });

                    if (result) {
                        // Convert compressed base64 back to File
                        const response = await fetch(result);
                        const blob = await response.blob();
                        const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });

                        newFiles.push(compressedFile);
                        newImages.push(result);
                    }
                }
            }

            if (newFiles.length > 0) {
                setGalleryFiles(prev => [...prev, ...newFiles]);
                setGalleryImages(prev => [...prev, ...newImages]);
                setFormData(prev => ({
                    ...prev,
                    gallery: [...prev.gallery, ...newImages],
                }));
            }
        }

        // Reset the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleAddImages = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRemoveImage = (imageUrl: string, index: number) => {
        setGalleryImages(prev => prev.filter((_, i) => i !== index));
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
        setFormData(prev => ({
            ...prev,
            gallery: prev.gallery.filter(url => url !== imageUrl),
        }));
    };

    return (
        <Card
            sx={{
                backgroundColor: theme[themeStyle].secondaryBg,
                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                borderRadius: 2,
            }}>
            <CardHeader
                title="Company Gallery"
                action={
                    isEditing && (
                        <Button
                            startIcon={<Plus size={16} />}
                            onClick={handleAddImages}
                            sx={{
                                color: theme[themeStyle].highlight,
                                borderColor: theme[themeStyle].highlight,
                                '&:hover': {
                                    borderColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].primary_orange,
                                },
                            }}
                            variant="outlined">
                            Add Images
                        </Button>
                    )
                }
                sx={{ color: theme[themeStyle].tag_white }}
                titleTypographyProps={{
                    sx: { fontFamily: FONT_ENUM.BOLD_HEADER },
                }}
            />
            <CardContent>
                {/* Hidden file input */}
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />

                {galleryImages.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 4,
                            color: theme[themeStyle].tag_white,
                            opacity: 0.7,
                        }}>
                        <ImageIcon size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            No images in gallery yet. Click "Add Images" to get started.
                        </Typography>
                    </Box>
                ) : (
                    <PhotoProvider>
                        <Grid container spacing={2}>
                            {galleryImages.map((imageUrl, index) => (
                                <Grid item xs={6} sm={4} md={3} key={index}>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            height: 200,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            backgroundColor: theme[themeStyle].deep_grey,
                                            border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                        }}>
                                        {/* Remove Button */}
                                        {isEditing && (
                                            <IconButton
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleRemoveImage(imageUrl, index);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                                    color: theme[themeStyle].tag_white,
                                                    width: 32,
                                                    height: 32,
                                                    zIndex: 3,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0,0,0,0.9)',
                                                    },
                                                }}>
                                                <X size={16} />
                                            </IconButton>
                                        )}

                                        {/* Image */}
                                        <PhotoView src={imageUrl}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    backgroundImage: `url(${imageUrl})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        </PhotoView>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </PhotoProvider>
                )}
            </CardContent>
        </Card>
    );
};

export { CompanyGalleryEdit };
